import React, { useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COLORS = {
  impressions: { stroke: "#22d3ee", fill: "#22d3ee" },  // cyan
  clicks:      { stroke: "#fbbf24", fill: "#fbbf24" },  // amber
  sales:       { stroke: "#a78bfa", fill: "#a78bfa" },  // violet
  revenue:     { stroke: "#34d399", fill: "#34d399" },  // emerald
};

const LABEL = {
  impressions: "Views",
  clicks: "Clicks",
  sales: "Sales",
  revenue: "Revenue",
};

const fmtCompact = (n) => {
  if (n == null) return "0";
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(Math.round(n));
};
const fmtCurrency = (n) => "$" + fmtCompact(n);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "numeric", day: "numeric", timeZone: "UTC" });
};

function normalizeSeries(series) {
  if (!series?.length) return [];
  const maxes = {
    impressions: Math.max(1, ...series.map((p) => p.impressions || 0)),
    clicks: Math.max(1, ...series.map((p) => p.clicks || 0)),
    sales: Math.max(1, ...series.map((p) => p.sales || 0)),
    revenue: Math.max(1, ...series.map((p) => p.revenue || 0)),
  };
  return series.map((p) => ({
    date: p.date,
    impressions: ((p.impressions || 0) / maxes.impressions) * 100,
    clicks: ((p.clicks || 0) / maxes.clicks) * 100,
    sales: ((p.sales || 0) / maxes.sales) * 100,
    revenue: ((p.revenue || 0) / maxes.revenue) * 100,
    raw: p,
  }));
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload?.raw || {};
  return (
    <div className="rounded-lg border border-white/15 bg-black/90 backdrop-blur-xl px-3 py-2.5 text-xs shadow-2xl min-w-[180px]">
      <div className="text-zinc-400 font-medium mb-1.5">{fmtDate(row.date)}</div>
      {["impressions", "clicks", "sales", "revenue"].map((k) => (
        <div key={k} className="flex items-center gap-2 py-0.5">
          <span className="w-2 h-2 rounded-full" style={{ background: COLORS[k].stroke }} />
          <span className="text-zinc-300">{LABEL[k]}</span>
          <span className="ml-auto text-white font-medium tabular-nums">
            {k === "revenue" ? fmtCurrency(row[k]) : fmtCompact(row[k])}
          </span>
        </div>
      ))}
    </div>
  );
}

function CustomLegend({ payload }) {
  return (
    <div className="flex items-center justify-center gap-5 flex-wrap text-xs">
      {payload.map((entry) => (
        <div key={entry.value} className="flex items-center gap-1.5 text-zinc-400">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: COLORS[entry.value]?.stroke }}
          />
          <span>{LABEL[entry.value]}</span>
        </div>
      ))}
    </div>
  );
}

export function OverlapChart({ data, rangeLabel }) {
  const normalized = useMemo(() => normalizeSeries(data), [data]);

  return (
    <div className="px-6 sm:px-8 pb-6 sm:pb-8" data-testid="overlap-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-white tracking-tight">Views, Clicks, Sales &amp; Revenue</h3>
        <span className="text-xs text-zinc-500">{rangeLabel} · normalized 0–100%</span>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={normalized} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
            <defs>
              {Object.entries(COLORS).map(([k, c]) => (
                <linearGradient key={k} id={`fill-${k}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={c.fill} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={c.fill} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={fmtDate}
              tick={{ fill: "#71717a", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
              minTickGap={22}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fill: "#71717a", fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={42}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(255,255,255,0.12)" }} />
            <Legend content={<CustomLegend />} verticalAlign="bottom" height={28} />
            {/* render order: weakest → strongest so subtle peaks stay visible */}
            {["revenue", "sales", "clicks", "impressions"].map((k) => (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                stroke={COLORS[k].stroke}
                strokeWidth={1.75}
                fill={`url(#fill-${k})`}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
